import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Target, Flame, Zap, BookOpen, ArrowRight, Home, Star } from 'lucide-react';
import { type ModuleData, type ModuleId } from '../../content';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { useAppStore } from '../../app/hooks/useAppStore';
import { SESSION_CONFIG } from '../../config/constants';
import { useDjaySelection, getDjayGreeting, DjayAvatar } from '../../mascot';
import type { DjayEmotion, DjayVariant } from '../../mascot';
import { APP_NAME } from '../../config/constants';
import { AdventureCTA } from './AdventureCTA';
import { WorldTile } from '../subjects/WorldTile';
import { WorldZone } from '../shared/WorldZone';
import { PageHero, HeroStatPill } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
interface HomePageProps {
  modules: ModuleData[];
  state: AppState;
  selectedGroup: GroupId;
  displayName: string;
  onStartPractice: (moduleId: ModuleId) => void;
  onNavigateToModules: () => void;
  onModuleSelect: (module: ModuleData) => void;
}
export function HomePage({
  modules,
  state,
  selectedGroup,
  displayName,
  onStartPractice,
  onNavigateToModules,
  onModuleSelect
}: HomePageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  // Time-based greeting — emotion sourced from getDjayGreeting() to stay in sync with djayMessages registry
  const hour = new Date().getHours();
  let greeting = 'Hallo';
  if (hour < 8) greeting = 'Goedemorgen';else if (hour < 12) greeting = 'Goedemorgen';else if (hour < 18) greeting = 'Goedemiddag';else greeting = 'Goedenavond';
  const djayEmotion: DjayEmotion = useMemo(() => getDjayGreeting(displayName).emotion,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [hour]);
  // ── Streak & Daily Goal — from AppState (no more local calculation) ──
  // Week activity: derive from dailyStreak state (data-driven, not scanning history)
  const currentWeekDays = state.dailyStreak?.currentWeekDays ?? 0;
  const currentStreak = currentWeekDays;
  // Today's answered questions — from DailyStreakState (tracked in useLearnerActions.recordAnswer)
  const todayQuestionsAnswered = state.dailyStreak?.todayQuestionsAnswered ?? 0;
  // Daily goal — user-configurable (5/10/20), fallback to SESSION_CONFIG
  const dailyGoal = state.dailyStreak?.dailyGoalTarget ?? SESSION_CONFIG.DAILY_GOAL;
  // Find the best module to continue with
  const suggestedModule = useMemo(() => {
    const inProgress = modules.filter(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      const answered = getAnsweredQuestions(m.id).length;
      return (answered > 0 || mastery > 0) && mastery < 95;
    });
    if (inProgress.length > 0) {
      return inProgress.reduce((prev, curr) => getModuleMasteryForGroup(curr.id) < getModuleMasteryForGroup(prev.id) ? curr : prev);
    }
    return modules[0];
  }, [modules, getModuleMasteryForGroup, getAnsweredQuestions]);
  const hasStarted = modules.some(m => {
    const answered = getAnsweredQuestions(m.id).length;
    return answered > 0;
  });
  return <PageContainer className="space-y-8 pb-20" data-id="element-1017">
      {/* HERO ZONE - Refactored to match SubjectsPage */}
      <motion.div variants={itemVariants} data-id="element-1018">
        <PageHero title={`${greeting}, ${displayName}!`} subtitle={`Klaar voor een nieuw avontuur in groep ${selectedGroup}? 🚀`} gradientClasses="from-orange-500 via-amber-500 to-yellow-500" djayEmotion={djayEmotion} djayVariant={selectedVariant} icon={Home} iconBgClass="bg-blue-400" floatingEmojis={['🚀', '⭐']} data-id="element-1019">
          <HeroStatPill icon={Target} data-id="element-1020">
            {todayQuestionsAnswered} / {dailyGoal} dagdoel
          </HeroStatPill>
          <HeroStatPill icon={Flame} data-id="element-1021">{currentStreak} dagen streak</HeroStatPill>
          <HeroStatPill icon={Star} data-id="element-1022">Level {state.stats.level}</HeroStatPill>
        </PageHero>
      </motion.div>

      {/* PRIMARY ACTION */}
      <motion.div variants={itemVariants} data-id="element-1023">
        <WorldZone icon={Zap} title="Jouw Avontuur" accentColor="bg-amber-100 text-amber-600" djayMessage="Kies een avontuur om mee te beginnen!" djayEmotion="excited" djayVariant={selectedVariant} isLast={false} data-id="element-1024">
          {hasStarted && suggestedModule ? <AdventureCTA module={suggestedModule} selectedGroup={selectedGroup} onStart={() => onStartPractice(suggestedModule.id)} getModuleMasteryForGroup={getModuleMasteryForGroup} djayVariant={selectedVariant} data-id="element-1025" /> : <WelcomeCard onStart={onNavigateToModules} djayVariant={selectedVariant} data-id="element-1026" />}
        </WorldZone>
      </motion.div>

      {/* SUBJECTS BUBBLES - Refactored to WorldZone + WorldTile */}
      <motion.div variants={itemVariants} data-id="element-1027">
        <WorldZone icon={BookOpen} title="Ontdek Werelden" accentColor="bg-orange-100 text-orange-600" djayMessage="Welke wereld gaan we vandaag ontdekken?" djayEmotion="excited" djayVariant={selectedVariant} isLast={true} data-id="element-1028">
          {/* Responsive Grid of Tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 sm:gap-6 justify-items-center mb-6" data-id="element-1029">
            {modules.slice(0, 5).map((module, index) => <WorldTile key={module.id} module={module} selectedGroup={selectedGroup} onClick={() => onModuleSelect(module)} getModuleMasteryForGroup={getModuleMasteryForGroup} getAnsweredQuestions={getAnsweredQuestions} index={index} data-id="element-1030" />)}
          </div>

          <div className="flex justify-center mt-8" data-id="element-1031">
            <Button variant="outline" size="md" onClick={onNavigateToModules} className="bg-white/80 backdrop-blur-sm hover:bg-white shadow-sm border-2 border-orange-100 text-orange-700 w-full sm:w-auto" data-id="element-1032">
              Alle werelden bekijken <ArrowRight className="w-4 h-4 ml-2" data-id="element-1033" />
            </Button>
          </div>
        </WorldZone>
      </motion.div>
    </PageContainer>;
}
// Welcome Card for new users - Refactored to be more playful
function WelcomeCard({
  onStart,
  djayVariant
}: {
  onStart: () => void;
  djayVariant: DjayVariant;
}) {
  return <Card className="p-8 sm:p-10 text-center bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-100 shadow-sm relative overflow-hidden rounded-[2rem]" data-id="element-1034">
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-1035" />

      <div className="relative z-10 flex flex-col items-center" data-id="element-1036">
        <motion.div animate={{
        y: [0, -8, 0]
      }} transition={{
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut'
      }} className="mb-6" data-id="element-1037">
          <DjayAvatar size="lg" emotion="celebrating" variant={djayVariant} animate data-id="element-1038" />
        </motion.div>

        <h2 className="text-2xl sm:text-3xl font-black text-slate-800 mb-3 tracking-tight" data-id="element-1039">
          Welkom bij {APP_NAME}!
        </h2>
        <p className="text-slate-600 text-base sm:text-lg mb-8 max-w-md mx-auto font-medium leading-relaxed" data-id="element-1040">
          Je avontuur begint hier. Kies een wereld om te starten met oefenen en
          verdien je eerste vlammetje! 🔥
        </p>
        <Button onClick={onStart} variant="primary" size="xl" className="shadow-md rounded-2xl" data-id="element-1041">
          <BookOpen className="w-6 h-6 mr-2 fill-current" data-id="element-1042" />
          Kies je eerste wereld
        </Button>
      </div>
    </Card>;
}